/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { parseStatus } from "@helpers/enumHelper";
import { getRolesFromLocations } from "@helpers/organizationHelper";
import { ExistingExaminee, UpcomingAssessment } from "@store/locationsSlice";
import _ from "lodash";
import Calendar, { OnChangeDateRangeCallback } from "react-calendar";
import { LBtn } from "../../../../../common/components/LBtn";
import { LCheckbox } from "../../../../../common/components/LCheckbox";
import { DropdownMenuOption } from "../../../../../common/components/LDropdownMenu";
import {
    AssessmentResultsCode,
    AssessmentStatus,
    ExistingAssessment,
    ExistingUser
} from "../../../../../common/store/generalTypes";

export interface AssessmentFilter {
    statuses: AssessmentStatus[];
    visits: number[];
    forms: string[];
    score: AssessmentResultsCode[];
    exceptions: number[];
    searchText: string;
    dateRange?: [Date] | [Date, Date];
}

export const AssessmentFilterListInitialState = {
    statuses: [] as AssessmentStatus[],
    exceptions: [] as number[],
    forms: [] as string[],
    visits: [] as number[],
    score: [] as AssessmentResultsCode[],
    searchText: ""
} as AssessmentFilter;

export const searchExistingUsersFilter = (searchText: string, data: ExistingUser[]): ExistingUser[] => {
    return data.filter(
        (d) =>
            d.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
            (d.firstName + " " + d.lastName).toLowerCase().includes(searchText.toLowerCase()) ||
            d.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.id.toString().includes(searchText) ||
            d.email.toString().includes(searchText) ||
            (d.locations &&
                getRolesFromLocations(d.locations).some((r) => r.toLowerCase().includes(searchText.toLowerCase()))) ||
            d.locations?.some((l) => l.name.toLowerCase().includes(searchText.toLowerCase())) ||
            d.status.toLowerCase().includes(searchText.toLowerCase())
    );
};

export const searchExamineesFilter = (searchText: string, data?: ExistingExaminee[]): ExistingExaminee[] => {
    if (!data?.length) {
        return [];
    }

    return data.filter(
        (d) =>
            d.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (d.firstName + " " + d.lastName).toLowerCase().includes(searchText.toLowerCase()) ||
            d.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.email?.toString().includes(searchText)
    );
};

export const searchExamineesListFilter = (searchText: string, data?: ExistingExaminee[]): ExistingExaminee[] => {
    if (!data?.length) {
        return [];
    }

    return data.filter(
        (d) =>
            d.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (d.firstName + " " + d.lastName).toLowerCase().includes(searchText.toLowerCase()) ||
            d.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.email?.toString().includes(searchText) ||
            d.clinicianTitle.toLowerCase().includes(searchText.toLowerCase()) ||
            d.clinicianLastName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.clinicianFirstName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.location.toLowerCase().includes(searchText)
    );
};

export const searchUpcomingAssessmentsFilter = (
    searchText: string,
    data?: UpcomingAssessment[]
): UpcomingAssessment[] => {
    if (!data?.length) {
        return [];
    }

    return data.filter(
        (d) =>
            d.examineeFirstName.toLowerCase().includes(searchText.toLowerCase()) ||
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (d.examineeFirstName + " " + d.examineeLastName).toLowerCase().includes(searchText.toLowerCase()) ||
            d.examineeLastName.toLowerCase().includes(searchText.toLowerCase()) ||
            d.examineeEmail?.toString().includes(searchText)
    );
};

export const searchCompletedAssessmentsFilter = (
    searchText: string,
    data?: ExistingAssessment[]
): ExistingAssessment[] => {
    if (!data?.length) {
        return [];
    }

    return data.filter(
        (d) =>
            d.examineeFirstName.toLowerCase().includes(searchText.toLowerCase()) ||
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (d.examineeFirstName + " " + d.examineeLastName).toLowerCase().includes(searchText.toLowerCase()) ||
            d.examineeLastName.toLowerCase().includes(searchText.toLowerCase())
    );
};

export const getFilteredData = (filter: AssessmentFilter, data: ExistingAssessment[]): ExistingAssessment[] => {
    let newData = _.cloneDeep(data);

    if (filter.searchText.length > 0) {
        newData = newData.filter(
            (d) =>
                d.examineeFirstName.toLowerCase().includes(filter.searchText.toLowerCase()) ||
                (d.examineeFirstName + " " + d.examineeLastName)
                    .toLowerCase()
                    .includes(filter.searchText.toLowerCase()) ||
                d.examineeLastName.toLowerCase().includes(filter.searchText.toLowerCase()) ||
                d.examineeId.toString().includes(filter.searchText)
        );
    }

    if (filter.statuses.length > 0) {
        newData = newData.filter((d) => filter.statuses.includes(d.status));
    }
    if (filter.visits.length > 0) {
        newData = newData.filter((d) => filter.visits.includes(d.visitNumber));
    }
    if (filter.forms.length > 0) {
        newData = newData.filter((d) => filter.forms.includes(d.formName));
    }
    if (filter.score.length > 0) {
        console.log(filter.score.toString().toLowerCase());
        newData = newData.filter(
            (d) =>
                filter.score.toString().toLowerCase().includes(d.score) ||
                (filter.score.includes(AssessmentResultsCode.Discontinued) && !d.score)
        );
    }
    if (filter.exceptions.length > 0) {
        newData = newData
            .filter((d) => d.exceptions !== undefined)
            .filter((d) => filter.exceptions.includes(d.exceptions ?? 0));
    }

    if (filter.dateRange) {
        if (filter.dateRange.length > 0) {
            const date = filter.dateRange[0];
            if (date) newData = newData.filter((d) => new Date(d.date) > date);
        }
        if (filter.dateRange.length === 2) {
            const date = filter.dateRange[1];
            if (date) newData = newData.filter((d) => new Date(d.date) < date);
        }
    }

    return newData;
};

export const getStatusList = (
    filter: AssessmentFilter,
    data: ExistingAssessment[],
    onChange?: React.ChangeEventHandler
): DropdownMenuOption[] => {
    if (!data) return [] as DropdownMenuOption[];
    const statuses = Array.from(new Set(data.map((d) => d.status)));
    if (!statuses) return [] as DropdownMenuOption[];
    return statuses.map(
        (s) =>
            ({
                optionName: s,
                children: (
                    <LCheckbox
                        inputName={"status-" + s}
                        label={`${parseStatus(s)} (${data.filter((d) => d.status === s).length.toString()})`}
                        value={s}
                        checked={filter ? filter.statuses.includes(s) : false}
                        onChange={onChange}
                    />
                )
            } as DropdownMenuOption)
    );
};

export const getVisitList = (
    filter: AssessmentFilter,
    data: ExistingAssessment[],
    onChange?: React.ChangeEventHandler
): DropdownMenuOption[] => {
    if (!data) return [] as DropdownMenuOption[];
    const visits = Array.from(new Set(data.map((d) => d.visitNumber)));
    if (!visits) return [] as DropdownMenuOption[];
    return visits.map(
        (s) =>
            ({
                optionName: s.toString(),
                children: (
                    <LCheckbox
                        inputName={"visit-" + s.toString()}
                        label={`${s} (${data.filter((d) => d.visitNumber === s).length.toString()})`}
                        value={s.toString()}
                        checked={filter ? filter.visits.includes(s) : false}
                        onChange={onChange}
                    />
                )
            } as DropdownMenuOption)
    );
};

export const getFormList = (
    filter: AssessmentFilter,
    data: ExistingAssessment[],
    onChange?: React.ChangeEventHandler
): DropdownMenuOption[] => {
    if (!data) return [] as DropdownMenuOption[];
    const forms = Array.from(new Set(data.map((d) => d.formName)));
    if (!forms) return [] as DropdownMenuOption[];
    return forms.map(
        (s) =>
            ({
                optionName: s.toString(),
                children: (
                    <LCheckbox
                        inputName={"form-" + s.toString()}
                        label={`${s} (${data.filter((d) => d.formName === s).length.toString()})`}
                        value={s.toString()}
                        checked={filter ? filter.forms.includes(s) : false}
                        onChange={onChange}
                    />
                )
            } as DropdownMenuOption)
    );
};

export const getScoreList = (
    filter: AssessmentFilter,
    data: ExistingAssessment[],
    onChange?: React.ChangeEventHandler
): DropdownMenuOption[] => {
    if (!data) return [] as DropdownMenuOption[];
    const newData = data.map((d) => {
        if (!d.score) return { ...d, score: AssessmentResultsCode.Discontinued };
        else return d;
    });
    const results = Array.from(new Set(newData.map((d) => d.score ?? AssessmentResultsCode.Discontinued)));
    if (!results) return [] as DropdownMenuOption[];
    return results.map(
        (s) =>
            ({
                optionName: s,
                children: (
                    <LCheckbox
                        inputName={"result-" + s}
                        label={`${_.startCase(s)} (${newData.filter((d) => d.score === s).length.toString()})`}
                        value={_.startCase(s)}
                        checked={filter ? filter.score.toString().toLowerCase().includes(s.toLowerCase()) : false}
                        onChange={onChange}
                    />
                )
            } as DropdownMenuOption)
    );
};

export const getExceptionsList = (
    filter: AssessmentFilter,
    data: ExistingAssessment[],
    onChange?: React.ChangeEventHandler
): DropdownMenuOption[] => {
    if (!data) return [] as DropdownMenuOption[];
    const exceptions = Array.from(new Set(data.map((d) => d.exceptions)));
    if (!exceptions) return [] as DropdownMenuOption[];
    return exceptions
        .filter((d) => d !== undefined)
        .map(
            (s) =>
                ({
                    optionName: s?.toString(),
                    children: (
                        <LCheckbox
                            inputName={"exception-" + (s?.toString() ?? "")}
                            label={`${s ?? ""} (${data.filter((d) => d.exceptions === s).length.toString()})`}
                            value={s?.toString()}
                            checked={filter ? filter.exceptions.includes(s ?? 0) : false}
                            onChange={onChange}
                        />
                    )
                } as DropdownMenuOption)
        );
};

export const getCalendar = (
    filter: AssessmentFilter,
    onChange?: OnChangeDateRangeCallback,
    onReset?: React.MouseEventHandler<HTMLButtonElement>
): DropdownMenuOption[] => {
    return [
        {
            optionName: "calendar",
            children: (
                <Calendar
                    selectRange={true}
                    onChange={onChange}
                    value={getDatesFromProp(filter.dateRange)}
                    formatMonthYear={(locale, date) => date.toLocaleString(locale, { month: "short", year: "numeric" })}
                />
            )
        },
        {
            optionName: "resetCalendar",
            children: (
                <LBtn variation="unstyled" onClick={onReset}>
                    Clear Selection
                </LBtn>
            )
        }
    ];
};

const getDatesFromProp = (
    dateRange: [Date] | [Date, Date] | undefined
): Date | [Date | null, Date | null] | null | undefined => {
    if (dateRange && dateRange.length === 1) return dateRange[0];
    if (dateRange && dateRange.length === 2) return [dateRange[0], dateRange[1]];
    return [null, null];
};
